import { absenceDayPartLabel } from '../../features/absence/public';
import type { ApiClient } from '../../api/client';
import type { StaffingWorker, TimeOffEntry } from '../../api/types';
import type { BulkReferenceField, BulkReferenceOption, BulkReferencePort } from '../../features/bulk/public';

type BulkReferenceClient = Pick<ApiClient, 'listAllWorkers' | 'listPeopleReferences' | 'listAllSupervisoryOrganizations' | 'listHireCompanies' | 'listHireLocations' | 'listHireJobProfiles' | 'listHireJobTypes' | 'listStaffingChanges' | 'listTimeOffEntries' | 'getTimeOffEntry' | 'listTimeOffTypes' | 'listTimeOffBalances'>;

const lifecycleLabels: Record<string, string> = { HIRE: '입사', LEAVE_OF_ABSENCE: '휴직', RETURN_FROM_LEAVE: '복직', END_WORKER: '고용 종료' };
const normalized = (value: string) => value.trim().toLocaleLowerCase();
const workerLabel = (worker: StaffingWorker) => worker.descriptor || '이름 미등록';
const workerDescription = (worker: StaffingWorker) => [worker.primaryJob?.businessTitle || worker.primaryJob?.jobProfile?.descriptor, worker.primaryJob?.supervisoryOrganization?.descriptor, worker.workerId].filter(Boolean).join(' · ');
const aliases = (...values: Array<string | undefined>) => [...new Set(values.filter((value): value is string => Boolean(value?.trim())))];

function entryOption(entry: TimeOffEntry, worker?: StaffingWorker): BulkReferenceOption {
  const name = worker ? workerLabel(worker) : entry.worker.descriptor || '구성원';
  const type = entry.timeOffType.descriptor || entry.descriptor || '휴가';
  const dates = entry.startDate === entry.endDate ? entry.startDate : `${entry.startDate} ~ ${entry.endDate}`;
  return {
    id: entry.id,
    label: `${name} · ${type} · ${dates}`,
    description: [absenceDayPartLabel(entry.dayPart), `${entry.quantity}${entry.unitOfTime === 'HOURS' ? '시간' : '일'}`, worker && workerDescription(worker)].filter(Boolean).join(' · '),
    searchText: [entry.descriptor, name, type, dates, worker?.workerId].filter(Boolean).join(' '),
    aliases: aliases(entry.descriptor),
  };
}

/** 한 대량 작업 창에서 같은 기준일·대상을 여러 행이 공유합니다. 닫았다가 열면 reset으로 재조회합니다. */
export function createBulkReferenceAdapter(client: BulkReferenceClient, can: (scope: string) => boolean): BulkReferencePort {
  const cache = new Map<string, Promise<unknown>>();
  function cached<T>(key: string, load: () => Promise<T>): Promise<T> {
    const previous = cache.get(key);
    if (previous) return previous as Promise<T>;
    const request = Promise.resolve().then(load).catch((error: unknown) => {
      if (cache.get(key) === request) cache.delete(key);
      throw error;
    });
    cache.set(key, request);
    return request;
  }
  function requireScope(scope: string, label: string) {
    if (!can(scope)) throw new Error(`${label} 조회 권한이 필요합니다.`);
  }
  const workers = (asOf: string) => {
    requireScope('staffing.read', '구성원');
    return cached(`workers:${asOf}`, () => client.listAllWorkers('', asOf).then(({ data }) => data));
  };

  async function options(field: BulkReferenceField, query: string, values: Record<string, string>): Promise<BulkReferenceOption[]> {
    const asOf = (values.effectiveDate || values.hireDate || values.startDate || values.effectiveOn || '').trim();
    if (field === 'workerId') return (await workers(asOf)).map((worker) => ({ id: worker.id, label: workerLabel(worker), description: workerDescription(worker), aliases: aliases(worker.descriptor, worker.workerId, worker.person?.email) }));
    if (field === 'personId') {
      requireScope('person.read', '개인');
      requireScope('person.name.read', '개인 이름');
      const people = await cached('people', () => client.listPeopleReferences().then(({ data }) => data));
      const linkedWorkers = can('staffing.read') ? await workers(asOf) : [];
      const workersByPerson = new Map<string, StaffingWorker[]>();
      for (const worker of linkedWorkers) {
        if (!worker.person?.id) continue;
        const linked = workersByPerson.get(worker.person.id) ?? [];
        linked.push(worker);
        workersByPerson.set(worker.person.id, linked);
      }
      return people.map((person) => {
        const linked = workersByPerson.get(person.id) ?? [];
        return { id: person.id, label: person.descriptor || linked[0]?.descriptor || '이름 미등록', description: linked.length ? linked.map(workerDescription).filter(Boolean).join(' / ') : '고용 정보 없음', aliases: aliases(person.descriptor, ...linked.flatMap((worker) => [worker.descriptor, worker.workerId, worker.person?.email])) };
      });
    }
    if (field === 'companyId' || field === 'locationId' || field === 'jobProfileId' || field === 'jobTypeId') {
      requireScope('staffing.read', '기준 정보');
      const workerType = values.workerType === 'CONTINGENT_WORKER' ? 'CONTINGENT_WORKER' : 'EMPLOYEE';
      const key = `${field}:${field === 'companyId' ? '' : asOf}:${field === 'jobTypeId' ? workerType : ''}`;
      const items = await cached(key, () => {
        if (field === 'companyId') return client.listHireCompanies();
        if (field === 'locationId') return client.listHireLocations(asOf);
        if (field === 'jobProfileId') return client.listHireJobProfiles(asOf);
        return client.listHireJobTypes(workerType, asOf);
      });
      return items.data.map((item) => ({ id: item.id, label: item.descriptor || '이름 미등록', aliases: aliases(item.descriptor) }));
    }
    if (field === 'organizationId') {
      requireScope('staffing.read', '조직');
      const organizations = await cached(`organizations:${asOf}`, () => client.listAllSupervisoryOrganizations(asOf).then(({ data }) => data));
      const byId = new Map(organizations.map((organization) => [organization.id, organization]));
      return organizations.map((organization) => {
        const path: string[] = [];
        const visited = new Set([organization.id]);
        let parentId = organization.parent?.id;
        while (parentId && !visited.has(parentId)) {
          visited.add(parentId);
          const parent = byId.get(parentId);
          if (!parent) break;
          path.unshift(parent.descriptor || parent.organizationCode || '이름 미등록');
          parentId = parent.parent?.id;
        }
        return { id: organization.id, label: organization.descriptor || organization.organizationCode || '이름 미등록', description: path.join(' / '), aliases: aliases(organization.descriptor, organization.organizationCode) };
      });
    }
    if (field === 'eventId') {
      requireScope('staffing.read', '인사 이력');
      const events = await cached('lifecycle-events', () => client.listStaffingChanges('', 'COMPLETED').then(({ data }) => data));
      return events.filter((event) => event.status === 'COMPLETED' && event.processType in lifecycleLabels).map((event) => {
        const name = event.worker?.descriptor || event.person?.descriptor || event.subject.descriptor || '구성원';
        const type = lifecycleLabels[event.processType];
        return { id: event.id, label: `${name} · ${type} · ${event.effectiveDate || '시행일 미등록'}`, description: event.descriptor, aliases: aliases(event.descriptor), searchText: [name, type, event.descriptor, event.effectiveDate].filter(Boolean).join(' ') };
      });
    }
    if (field === 'timeOffTypeId') {
      requireScope('absence.read', '휴가 유형');
      const types = await cached('time-off-types', () => client.listTimeOffTypes().then(({ data }) => data));
      return types.filter((type) => type.status === 'ACTIVE' && (!asOf || ((!type.effectiveFrom || type.effectiveFrom <= asOf) && (!type.effectiveTo || asOf < type.effectiveTo)))).map((type) => ({ id: type.id, label: type.descriptor, description: type.allowedUnit === 'HOURS' ? '시간 단위' : '일 단위', aliases: aliases(type.descriptor, type.code) }));
    }
    if (field === 'entitlementId') {
      requireScope('absence.read', '휴가 잔액');
      if (!values.workerId) throw new Error('휴가 잔액을 검색하려면 구성원을 먼저 선택해 주세요.');
      const balances = await cached(`balances:${values.workerId}:${asOf}`, () => client.listTimeOffBalances(values.workerId, asOf || undefined).then(({ data }) => data));
      return balances.map((balance) => ({ id: balance.entitlement.id, label: balance.timeOffType.descriptor || balance.timeOffPlan.descriptor || balance.entitlement.descriptor || '휴가 잔액', description: [balance.timeOffPlan.descriptor, `사용 가능 ${balance.available}${balance.unitOfTime === 'HOURS' ? '시간' : '일'}`, balance.asOf].filter(Boolean).join(' · '), aliases: aliases(balance.entitlement.descriptor, balance.timeOffType.descriptor, balance.timeOffPlan.descriptor) }));
    }
    requireScope('absence.read', '휴가 기록');
    if (values.workerId) {
      const entries = await cached(`entries:${values.workerId}`, () => client.listTimeOffEntries(values.workerId).then(({ data }) => data));
      const worker = can('staffing.read') ? (await workers(asOf)).find((item) => item.id === values.workerId) : undefined;
      return entries.filter((entry) => entry.status === 'CONFIRMED').map((entry) => entryOption(entry, worker));
    }
    if (!query.trim()) throw new Error('휴가 기록을 검색하려면 구성원을 먼저 선택해 주세요.');
    try {
      const entry = await cached(`entry:${query.trim()}`, () => client.getTimeOffEntry(query.trim()));
      return entry.status === 'CONFIRMED' ? [entryOption(entry)] : [];
    } catch (error) {
      if (error && typeof error === 'object' && 'status' in error && (error.status === 400 || error.status === 404)) throw new Error('휴가 기록을 이름으로 검색하려면 구성원을 먼저 선택해 주세요.');
      throw error;
    }
  }
  return {
    reset: () => cache.clear(),
    async search(field, query, values) {
      if (field === 'locationCode' || field === 'jobFamilyCode') throw new Error('상위 기준정보는 기준정보 등록 화면에서 선택해 주세요.');
      if (field === 'personId') { requireScope('person.read', '개인'); requireScope('person.name.read', '개인 이름'); }
      else if (['timeOffTypeId', 'entitlementId', 'entryId'].includes(field)) requireScope('absence.read', '휴가');
      else requireScope('staffing.read', field === 'workerId' ? '구성원' : '기준 정보');
      const context = [field, values.effectiveDate || values.hireDate || values.startDate || values.effectiveOn || '',
        field === 'entitlementId' || field === 'entryId' ? values.workerId || '' : '',
        field === 'jobTypeId' ? values.workerType || 'EMPLOYEE' : '', can('staffing.read'),
        field === 'entryId' && !values.workerId ? query.trim() : ''];
      const items = await cached(`options:${JSON.stringify(context)}`, () => options(field, query, values));
      const search = normalized(query);
      return search ? items.filter((item) => [item.id, item.label, item.description, item.searchText, ...(item.aliases ?? [])].filter(Boolean).some((value) => normalized(value!).includes(search))) : items;
    },
  };
}
